import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { startCheckout } from '@/lib/stripe';
import { authErrorMessage } from '@/lib/auth';
import type { TierId } from '@/types';

function isTier(value: string | null): value is TierId {
  return value === 'garage' || value === 'restorer' || value === 'pro_shop';
}

const TIER_LABEL: Record<TierId, string> = {
  garage: 'Garage',
  restorer: 'Restorer',
  pro_shop: 'Pro Shop',
};

export default function Subscribe() {
  const { user, loading: authLoading } = useAuth();
  const { isActive, loading: subLoading } = useSubscription();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  const tier = params.get('tier');

  useEffect(() => {
    if (authLoading || subLoading) return;
    if (!user) {
      navigate(`/signin?from=${encodeURIComponent(`/subscribe?tier=${tier ?? ''}`)}`, { replace: true });
      return;
    }
    if (isActive) {
      navigate('/account', { replace: true });
      return;
    }
    if (!isTier(tier)) {
      setError(`Unknown tier "${tier}". Pick a plan from the Pricing page.`);
      setBusy(false);
      return;
    }
    (async () => {
      try {
        const url = await startCheckout(tier);
        window.location.href = url;
      } catch (err) {
        setError(authErrorMessage(err));
        setBusy(false);
      }
    })();
  }, [user, authLoading, isActive, subLoading, tier, navigate]);

  if (!authLoading && !subLoading && isActive) {
    return <Navigate to="/account" replace />;
  }

  return (
    <div className="app">
      <div className="main">
        <div style={{ maxWidth: 520, margin: '60px auto', textAlign: 'center' }}>
          <div className="eyebrow">Stripe Checkout</div>
          <h1 className="h-display" style={{ fontSize: 48 }}>
            {isTier(tier) ? `Subscribing to ${TIER_LABEL[tier]}` : 'Starting checkout'}
          </h1>
          {error ? (
            <>
              <div className="auth-error" style={{ marginBottom: 18, textAlign: 'left' }}>{error}</div>
              <button type="button" className="btn" onClick={() => navigate('/pricing')}>
                ← Back to Pricing
              </button>
            </>
          ) : (
            <p className="lead" style={{ margin: '12px auto 24px' }}>
              {busy
                ? 'Opening Stripe Checkout in test mode. Use card 4242 4242 4242 4242, any future expiry, any CVC.'
                : 'Redirecting…'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
